import { NostrEvent } from "@nostrify/nostrify";
import { ScryfallCard, ScryfallCardWithCount } from "@/types/scryfall";
import { isScryfallFormat, ScryfallFormat } from "@/lib/formats";

export const DECK_KIND = 37381;

export type Card = {
  name: string;
  quantity: number;
  set_id?: string;
  art_id?: string;
  foil?: string;
  lang?: string;
  scryfallCard?: ScryfallCard;
};

export type Deck = {
  event: NostrEvent;
  title: string;
  commanders: string[];
  companion?: string | null;
  cards: Card[];
  sideboard: Card[];
};

export function isDeckEvent(event: NostrEvent): boolean {
  if (event.kind !== DECK_KIND) return false;

  const title = event.tags.find((t) => t[0] === "title")?.[1];
  const d = event.tags.find((t) => t[0] === "d")?.[1];
  const cards = event.tags.filter((t) => t[0] === "c");

  if (!title || !d || cards.length === 0) return false;

  return true;
}

export function isFormatTag(tag: string[]): tag is ["t", ScryfallFormat] {
  return tag[0] === "t" && isScryfallFormat(tag[1]);
}

export function transformDeckEvent(event: NostrEvent): Deck {
  const title = event.tags.find((t) => t[0] === "title")?.[1] || "";
  const commanders = event.tags.filter((t) => t[0] === "C").map((t) => t[1]);
  const companion = event.tags.find((t) => t[0] === "S")?.[1];
  const cards = event.tags
    .filter((t) => t[0] === "c")
    .map((c) => ({
      name: c[1],
      quantity: parseInt(c[2], 10),
      set_id: c[3],
      art_id: c[4],
      lang: c[5] || 'en',
      foil: c[6] === "foil" ? "foil" : "",
    }));

  const sideboard = event.tags
    .filter((t) => t[0] === "b")
    .map((c) => ({
      name: c[1],
      quantity: parseInt(c[2], 10),
      set_id: c[3],
      art_id: c[4],
      lang: c[5] || 'en',
      foil: c[6] === "foil" ? "foil" : "",
    }));

  return {
    event,
    title,
    commanders,
    companion,
    cards,
    sideboard,
  };
}

export function prepareDeckEvent(
  deck: Deck | null,
  title: string,
  format: string,
  cards: ScryfallCard[],
  sideboardCards: ScryfallCard[],
  commanderCardNames: string[],
  companionCardName: string | null,
  bannerUrl?: string | null,
  visibility: 'public' | 'unlisted' = 'public'
) {
  const deckTitle = title || "Untitled Deck";

  // Extract tags that are not kind 37381 specific
  const newTags: string[][] = deck
    ? deck.event.tags.filter((t) => {
      if (t[0] === "t" && isScryfallFormat(t[1])) {
        return false; // Exclude existing format tags
      }
      if (t[0] === 't' && t[1] === 'unlisted') {
        return false; // Exclude existing unlisted tags
      }
      return !["c", "title", "banner", "C", "S", "alt"].includes(t[0]);
    })
    : [];

  if (!deck || !deck.event.tags.find((t) => t[0] === "d")) {
    const dTag = `${deckTitle.toLowerCase().replace(/ /g, "-")}-${Math.floor(Date.now() / 1000)}`;
    newTags.push(["d", dTag]);
  }

  newTags.push(["title", deckTitle]);

  if (format && isScryfallFormat(format)) {
    newTags.push(["t", format]);
  }

  if (visibility === 'unlisted') {
    newTags.push(['t', 'unlisted']);
  }

  const commanderCards = cards.filter((c) => commanderCardNames.includes(c.name));
  const companionCard = cards.find((c) => c.name === companionCardName);

  if (bannerUrl) {
    newTags.push(["banner", bannerUrl]);
  } else {
    const existingBanner = deck?.event.tags.find(t => t[0] === 'banner');
    if (existingBanner) {
      newTags.push(existingBanner);
    } else {
      if (commanderCards.length > 0) {
        const artUrl =
          commanderCards[0].image_uris?.art_crop ||
          commanderCards[0].image_uris?.large ||
          commanderCards[0].image_uris?.normal;

        if (artUrl) {
          newTags.push(["banner", artUrl]);
        }
      } else if (cards.length > 0) {
        const firstCard = cards[0];
        const artUrl =
          firstCard.image_uris?.art_crop ||
          firstCard.image_uris?.large ||
          firstCard.image_uris?.normal;

        if (artUrl) {
          newTags.push(["banner", artUrl]);
        }
      }
    }
  }

  commanderCards.forEach((commander) => {
    newTags.push(["C", commander.name]);
  });

  if (companionCard) {
    newTags.push(["S", companionCard.name]);
  }

  Object.values(cards).forEach((card) => {
    const cardTag = [
      "c",
      card.name,
      ((card as ScryfallCardWithCount).count || 1).toString(),
      card.set,
      card.collector_number,
      card.lang,
      (card as ScryfallCardWithCount).is_foil ? "foil" : "",
    ];
    newTags.push(cardTag);
  });

  Object.values(sideboardCards).forEach((card) => {
    const cardTag = [
      "b",
      card.name,
      ((card as ScryfallCardWithCount).count || 1).toString(),
      card.set,
      card.collector_number,
      card.lang,
      (card as ScryfallCardWithCount).is_foil ? "foil" : "",
    ];
    newTags.push(cardTag);
  });


  newTags.push(["alt", `Magic: The Gathering Deck - ${deckTitle}`]);

  return {
    kind: deck ? deck.event.kind : DECK_KIND,
    tags: newTags,
    content: deck ? deck.event.content : "",
  };
}
